// TODO this should be moved to a common location, as mentions now uses this
// component.
// js/utils/components (common components) - Ian
import React, { useState } from 'react';
import styled from 'astroturf/react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckSquare, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { faSquare } from '@fortawesome/pro-light-svg-icons';

import { FilterOption } from './util';
import commaize from 'js/utils/commaize';

type Props<T> = {
  /**
   * Only show `limit` number of options with a Show More button
   * Defaults to infinity
   */
  limit?: number;
  /**
   * How many items to add when clicking "More", defaults to Infinity
   */
  showMoreLimit?: number;
  /**
   * Use a more descriptive variant of the show more button (defaults to false)
   */
  descriptiveShowMore?: boolean;
  /**
   * Whether this filter should be multiselect or not (defaults to false).  When
   * multi is present, we show checkboxes.  Otherwise, we only allow a single
   * selection at a time.  If there is an icon in the associated FilterOption,
   * then we display an icon -- and if there is no icon, we fallback to a status
   * radio button (complementing the multi default of checkboxes)
   *
   * Note that if there is not an icon in the associated FilterOption
   */
  multi?: boolean;
  /**
   * Keep resetAll null (the default) to not show "select all" and "clear all"
   * buttons to the user.  Otherwise, resetAll is a callback -- clear is true if
   * the user clicked "clear all" and false if they clicked "select all".
   */
  resetAll?: (clear: boolean) => void;
  onChange: (name: string) => void;
  options: FilterOption<T>[];
  selected: Set<T>;
  title: string;
  disableZeroes?: boolean;
};

const Container = styled.section`
  width: 100%;

  &:not(:last-of-type) {
    margin-bottom: 34px;
  }
`;

const Count = styled.p<{ selected?: boolean }>`
  color: #777;
  flex-grow: 1;
  text-align: right;
  margin: 0;

  // Have to apply here as well as the li
  // since this has a lighter color
  &.selected {
    color: #094ec3;
  }
`;

const Icon = styled(FontAwesomeIcon)<{ isDisabled?: boolean }>`
  color: #094ec3;

  &.isDisabled {
    color: #c4c4c4;
  }

  margin-right: 12px;
  width: 15.75px !important;
`;

const Name = styled.p`
  margin: 0;
`;

const OptionContainer = styled.li<{
  isSelected?: boolean;
  isDisabled?: boolean;
}>`
  align-items: center;
  color: #555;
  cursor: pointer;
  display: flex;

  &.isSelected {
    color: #094ec3;
    font-weight: 700;
  }

  &.isDisabled {
    color: #c4c4c4;
    pointer-events: none;
  }

  &:not(:last-of-type) {
    margin-bottom: 10px;
  }
`;

const Options = styled.ul`
  padding: 0;
  list-style: none;
  margin: 0;
`;

const TextButton = styled.button`
  background: transparent;
  border: 0;
  color: #094ec3;
  cursor: pointer;
  &.disabled {
    color: #9e9e9e;
    cursor: default;
  }
  font-weight: 400;
  margin-top: 6px;
  padding: 4px 0;
`;

const DividingTextLine = styled.span`
  display: inline-block;
  border: 1px solid #cfcfcf;
  width: 0px;
  height: 20px;
  position: relative;
  top: 5px;
  margin-right: 8px;
  margin-left: 8px;
  transform: rotate(90);
`;

export const Title = styled.h2`
  // This is so the title will take up the entire width
  // when used inside the MobileChipize component
  align-self: stretch;
  border-bottom: 1px solid #cfcfcf;
  color: #444;
  font-size: 12px;
  font-weight: 400;
  margin: 0 0 14px;
  padding-bottom: 5px;
  text-transform: uppercase;
`;

const ShowMore: React.FC<{
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  descriptive?: boolean;
  title?: string;
  numHidden?: number;
}> = ({ onClick, descriptive = false, title, numHidden }) => {
  if (descriptive && title && numHidden) {
    // TODO note the spacing is currently broken for this view
    return (
      <TextButton onClick={onClick} className="u-ml3x">
        <Name>
          Show {numHidden} more {title.toLowerCase()}
        </Name>
      </TextButton>
    );
  } else {
    return (
      <TextButton onClick={onClick}>
        <Icon icon={faPlus} />
        more
      </TextButton>
    );
  }
};

const Filter: React.FC<Props<string>> = ({
  limit: initialLimit = Infinity,
  descriptiveShowMore = false,
  showMoreLimit = Infinity,
  multi = false,
  resetAll,
  onChange,
  options,
  selected,
  title,
  disableZeroes,
}) => {
  const isSelected = (o: FilterOption<string>) => selected.has(o.value);

  const [limit, setLimit] = useState(initialLimit);
  const limited = options.slice(0, limit);

  // we don't need to calculate this variable unless resetAll is defined
  let numCheckedOptions = 0;
  if (resetAll) {
    // of all the checkboxes in this filter, how many are checked?
    numCheckedOptions = options.reduce(
      (sum, o) => sum + Number(isSelected(o)),
      0,
    );
  }

  return (
    <Container>
      <Title>{title}</Title>
      {resetAll && (
        <>
          <TextButton
            disabled={numCheckedOptions === options.length}
            onClick={() => resetAll(false)}
          >
            Select All
          </TextButton>
          <DividingTextLine />
          <TextButton
            disabled={numCheckedOptions === 0}
            onClick={() => resetAll(true)}
          >
            Clear All
          </TextButton>
        </>
      )}
      <Options>
        {limited.map((option) => {
          const isSelected = selected.has(option.value);
          const isDisabled = disableZeroes && !option.count;
          return (
            <OptionContainer
              key={option.name}
              onClick={() => onChange(option.value)}
              isSelected={isSelected}
              isDisabled={isDisabled}
            >
              {!multi &&
                ((isSelected && option.selectedIcon && (
                  <Icon icon={option.selectedIcon} isDisabled={isDisabled} />
                )) ||
                  (!isSelected && option.icon && (
                    <Icon icon={option.icon} isDisabled={isDisabled} />
                  )))}
              {multi && (
                <Icon
                  icon={isSelected ? faCheckSquare : faSquare}
                  isDisabled={isDisabled}
                />
              )}
              <Name>{option.name}</Name>
              {option.count != null && (
                <Count selected={isSelected}>{commaize(option.count)}</Count>
              )}
            </OptionContainer>
          );
        })}
      </Options>
      {/* TODO move this into an OptionContainer to fix spacing */}
      {limit < options.length && (
        <ShowMore
          onClick={() => setLimit(limit + showMoreLimit)}
          descriptive={descriptiveShowMore}
          title={title}
          numHidden={options.length - limit}
        />
      )}
    </Container>
  );
};

export default Filter;
